// src/components/scheduler/LeadCaptureForm.tsx
import React, { useState, useEffect } from 'react';
import { useScheduler } from './SchedulerContext';
import { LeadFormData } from './types';

interface LeadCaptureFormProps {
    onComplete: () => void;
}

export const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({ onComplete }) => {
    const { setLeadData } = useScheduler();
    const [formExpanded, setFormExpanded] = useState(false);
    const [errors, setErrors] = useState<Partial<LeadFormData>>({});
    const [robotAnswer, setRobotAnswer] = useState<string>('');

    const [formData, setFormData] = useState<LeadFormData>({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        employeeCount: '',
        averageRevenue: '',
        growthMotivation: '',
        websiteUrl: ''
    });

    // Check if last name has been started to expand form
    useEffect(() => {
        if (formData.lastName.length >= 1) {
            setFormExpanded(true);
        }
    }, [formData.lastName]);

    const formatPhoneNumber = (value: string) => {
        const phoneNumber = value.replace(/\D/g, '');
        const phoneNumberLength = phoneNumber.length;

        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        }
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhoneNumber(e.target.value);
        setFormData({ ...formData, phone: formatted });
        if (errors.phone) setErrors({ ...errors, phone: '' });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name as keyof LeadFormData]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<LeadFormData> = {};

        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (formData.phone.replace(/\D/g, '').length < 10) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        if (!formData.employeeCount) {
            newErrors.employeeCount = 'Please select employee count';
        }
        if (!formData.averageRevenue) {
            newErrors.averageRevenue = 'Please select average revenue';
        }
        if (!formData.growthMotivation) {
            newErrors.growthMotivation = 'Please select growth motivation';
        }
        if (!formData.websiteUrl.trim()) {
            newErrors.websiteUrl = 'Website URL is required';
        }
        if (robotAnswer !== 'no') {
            alert('Please confirm you are not a robot by selecting "No"');
            return false;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            setLeadData(formData);
            onComplete();
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#212A37] mb-2">
                    Swift Lead Digital Demo
                </h2>
                <p className="text-gray-600 text-sm">
                    This is a quick 30 minute demo. This lets us customize your Swift Lead Digital account and show how we can get you 5-10 more calls per week.
                </p>
            </div>

            {/* Initial Fields */}
            <div className="space-y-4">
                {/* Email - First field with pulsating border */}
                <div>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent transition-all ${!formData.email ? 'animate-pulse-border' : ''
                            } ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="your@email.com"
                    />
                    {errors.email && (
                        <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                    )}
                </div>

                {/* First Name and Last Name */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="First Name"
                        />
                        {errors.firstName && (
                            <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                        )}
                    </div>

                    <div>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="Last Name"
                        />
                        {errors.lastName && (
                            <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                        )}
                    </div>
                </div>

                {/* Terms text - shown only when lastName is empty */}
                {!formData.lastName && (
                    <p className="text-xs text-gray-500 mt-2">
                        By entering information, I agree to Terms & Privacy Policy
                    </p>
                )}
            </div>

            {/* Expanded Form Fields */}
            {formExpanded && (
                <div className="space-y-4 animate-fadeIn">
                    {/* Phone Number - First in expanded section */}
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="flex">
                            <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                                <span className="text-gray-500">🇺🇸</span>
                                <span className="ml-2 text-gray-700">+1</span>
                            </div>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handlePhoneChange}
                                className={`flex-1 px-4 py-3 border rounded-r-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent ${errors.phone ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="(555) 123-4567"
                            />
                        </div>
                        {errors.phone && (
                            <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 mb-1">
                            URL for the Website to Track <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="url"
                            id="websiteUrl"
                            name="websiteUrl"
                            value={formData.websiteUrl}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent ${errors.websiteUrl ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="https://example.com"
                        />
                        {errors.websiteUrl && (
                            <p className="mt-1 text-sm text-red-500">{errors.websiteUrl}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="employeeCount" className="block text-sm font-medium text-gray-700 mb-1">
                            How many employees are in your company? <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="employeeCount"
                            name="employeeCount"
                            value={formData.employeeCount}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent ${errors.employeeCount ? 'border-red-500' : 'border-gray-300'
                                }`}
                        >
                            <option value="">Select employee count</option>
                            <option value="1-5">1-5 employees</option>
                            <option value="6-10">6-10 employees</option>
                            <option value="11-25">11-25 employees</option>
                            <option value="26-50">26-50 employees</option>
                            <option value="51-100">51-100 employees</option>
                            <option value="100+">100+ employees</option>
                        </select>
                        {errors.employeeCount && (
                            <p className="mt-1 text-sm text-red-500">{errors.employeeCount}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="averageRevenue" className="block text-sm font-medium text-gray-700 mb-1">
                            What is your average revenue per month? <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="averageRevenue"
                            name="averageRevenue"
                            value={formData.averageRevenue}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent ${errors.averageRevenue ? 'border-red-500' : 'border-gray-300'
                                }`}
                        >
                            <option value="">Select average revenue</option>
                            <option value="<$10k">Less than $10k</option>
                            <option value="$10k-$25k">$10k - $25k</option>
                            <option value="$25k-$50k">$25k - $50k</option>
                            <option value="$50k-$100k">$50k - $100k</option>
                            <option value="$100k-$250k">$100k - $250k</option>
                            <option value="$250k+">$250k+</option>
                        </select>
                        {errors.averageRevenue && (
                            <p className="mt-1 text-sm text-red-500">{errors.averageRevenue}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="growthMotivation" className="block text-sm font-medium text-gray-700 mb-1">
                            Why do you want to grow your business? <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="growthMotivation"
                            name="growthMotivation"
                            value={formData.growthMotivation}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-[#e2ba57] focus:border-transparent ${errors.growthMotivation ? 'border-red-500' : 'border-gray-300'
                                }`}
                        >
                            <option value="">Select your motivation</option>
                            <option value="Increase Revenue">Increase Revenue</option>
                            <option value="Expand Market">Expand Market Reach</option>
                            <option value="Improve Efficiency">Improve Efficiency</option>
                            <option value="Scale Operations">Scale Operations</option>
                            <option value="Build Brand">Build Brand Recognition</option>
                            <option value="Other">Other</option>
                        </select>
                        {errors.growthMotivation && (
                            <p className="mt-1 text-sm text-red-500">{errors.growthMotivation}</p>
                        )}
                    </div>

                    {/* Are you a robot question */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Are You A Robot? <span className="text-red-500">*</span>
                        </label>
                        <div className="space-x-6 flex items-center">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="robot"
                                    value="yes"
                                    checked={robotAnswer === 'yes'}
                                    onChange={(e) => setRobotAnswer(e.target.value)}
                                    className="mr-2 text-[#e2ba57] focus:ring-[#e2ba57]"
                                />
                                <span className="text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="robot"
                                    value="maybe"
                                    checked={robotAnswer === 'maybe'}
                                    onChange={(e) => setRobotAnswer(e.target.value)}
                                    className="mr-2 text-[#e2ba57] focus:ring-[#e2ba57]"
                                />
                                <span className="text-gray-700">Maybe</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="robot"
                                    value="no"
                                    checked={robotAnswer === 'no'}
                                    onChange={(e) => setRobotAnswer(e.target.value)}
                                    className="mr-2 text-[#e2ba57] focus:ring-[#e2ba57]"
                                />
                                <span className="text-gray-700">No</span>
                            </label>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-[#e2ba57] text-[#212A37] py-3 px-6 rounded-md font-semibold hover:bg-[#d1a43f] transition-colors"
                    >
                        Continue
                    </button>
                </div>
            )}
        </form>
    );
};